import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PageTitle,
  CommonGrid,
  HeaderContainer,
} from '../../../styledComponents/common';

import TopPagination from '../../../components/Pagination/TopPagination/TopPagination';
import BottomPagination from '../../../components/Pagination/BottomPagination/BottomPagination';

import MembersFilter from '../MembersFilter';
import PendingRequestMemberTable from './PendingRequestMemberTable';

import Plus from '../../../assets/img/icons/plus_white.svg';
import { ButtonIcon, ColoredButtonWithIcon } from 'styledComponents/buttons';
import PendingFilters from './PendingFilters';

const PendingRequests = ({
  officesShortList,
  getOfficesShortList,
  getMembersList,
  history,
  getInvitedMembersList,
  membersList,
  isLoading,
  updateMember,
  updatedData,
  // pagination
  totalMembersCount,
  pageSize,
  location,
  selectedOrganization,
  getPendingRequests,
  pendingRequestsData,
  pendingRequestsLoading,
  bulkUpdateFromPendingRequests,
  bulkUpdateFromPendingRequestsLoading,
}) => {
  const { t } = useTranslation();

  // State variables
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [userCodeSearchTerm, setUserCodeSearchTerm] = useState('');
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedEmployeeType, setSelectedEmployeeType] = useState(null);
  const [memberList, setMemberList] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState({ label: t('pending'), value: 'pending' });

  // Fetch data on component mount
  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id) {
      getOfficesShortList(selectedOrganization.id);

      // Initialize with 'pending' status
      setSelectedStatus({ label: t('pending'), value: 'pending' });

      // Fetch pending requests
      const payload = {
        organization_id: selectedOrganization.id,
        page: 1,
        status: 'pending'
      };

      getPendingRequests(payload);
    }
  }, [selectedOrganization]);

  // Update pendingRequests when pendingRequestsData changes
  useEffect(() => {
    if (pendingRequestsData) {
      console.log('Pending Requests Data:', pendingRequestsData);
      setPendingRequests(pendingRequestsData);
    }
  }, [pendingRequestsData]);

  // Fetch pending requests with filters
  const fetchPendingRequests = (page = 1) => {
    if (selectedOrganization && selectedOrganization.id) {
      const payload = {
        organization_id: selectedOrganization.id,
        page,
      };

      // Always include the current status
      if (selectedStatus && selectedStatus.value) {
        payload.status = selectedStatus.value;
      }

      if (selectedOffice && selectedOffice.value) {
        console.log("selectedOffice", selectedOffice)
        payload.office_id = selectedOffice.value;
      }

      if (selectedRole && selectedRole.value) {
        payload.role = selectedRole.value;
      }

      if (searchTerm && searchTerm.trim() !== '') {
        payload.name_email = searchTerm.trim();
      }

      if (userCodeSearchTerm && userCodeSearchTerm.trim() !== '') {
        payload.user_id = userCodeSearchTerm.trim();
      }

      console.log('Fetching pending requests with payload:', payload);
      getPendingRequests(payload);
    }
  };

  // Handle page change
  const handlePageChange = page => {
    setCurrentPageNumber(page);
    fetchPendingRequests(page);
  };

  // Handle search by name
  const handleSearchByName = () => {
    setCurrentPageNumber(1);
    fetchPendingRequests(1);
  };

  // Clear name search
  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPageNumber(1);
    fetchPendingRequests(1);
  };

  // Handle search by user code
  const handleSearchByUserCode = () => {
    setCurrentPageNumber(1);
    fetchPendingRequests(1);
  };


  // Clear user code search
  const handleClearUserCodeSearch = () => {
    setUserCodeSearchTerm('');
    setCurrentPageNumber(1);
    fetchPendingRequests(1);
  };

  // Handle office selection
  const selectOffice = office => {
    console.log("office", office)
    let payload = {
      organization_id: selectedOrganization.id,
      page: 1,
    };
    if (office && office.value) {
      payload.office_id = office.value;
    }
    if (selectedStatus && selectedStatus.value) {
      payload.status = selectedStatus.value;
    }
    if (selectedRole && selectedRole.value) {
      payload.role = selectedRole.value;
    }
    if (searchTerm && searchTerm.trim() !== '') {
      payload.name_email = searchTerm.trim();
    }
    if (userCodeSearchTerm && userCodeSearchTerm.trim() !== '') {
      payload.user_id = userCodeSearchTerm.trim();
    }
    getPendingRequests(payload);
  };

  // Handle role selection
  const handleRoleChange = role => {
    setSelectedRole(role);
    setCurrentPageNumber(1);
    setTimeout(() => {
      fetchPendingRequests(1);
    }, 100);
  };

  // Handle search term change
  const onSearchTermChange = e => {
    setSearchTerm(e.target.value);
  };

  // Handle user code search term change
  const onUserCodeSearchTermChange = e => {
    setUserCodeSearchTerm(e.target.value);
  };

  // Handle selection change
  const handleSelectionChange = (selectedIds) => {
    setSelectedMembers(selectedIds);
  };

  // Handle approve
  const handleApprove = () => {
    console.log('Approve:', selectedMembers);
  };

  // Handle reject
  const handleReject = () => {
    console.log('Reject:', selectedMembers);
  };

  // Handle status change
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setCurrentPageNumber(1);

    // Use setTimeout to ensure state is updated before fetching
    setTimeout(() => {
      const payload = {
        organization_id: selectedOrganization.id,
        page: 1,
        status: status.value,
      };

      if (selectedOffice && selectedOffice.value) {
        payload.office_id = selectedOffice.value;
      }

      if (selectedRole && selectedRole.value) {
        payload.role = selectedRole.value;
      }

      if (searchTerm && searchTerm.trim() !== '') {
        payload.name_email = searchTerm.trim();
      }

      if (userCodeSearchTerm && userCodeSearchTerm.trim() !== '') {
        payload.user_id = userCodeSearchTerm.trim();
      }

      getPendingRequests(payload);
    }, 100);
  };

  return (
    <div className="content">
      <CommonGrid alignItem="center">
        <PageTitle>{t('pending_requests')}</PageTitle>
        {localStorage.getItem('user_role') === 'admin' &&
          !localStorage.getItem('viewer_role') && (
            <ColoredButtonWithIcon
              onClick={() => history.push('/user/member-add-update')}
            >
              <ButtonIcon src={Plus} />
              {t('add_user')}
            </ColoredButtonWithIcon>
          )}
      </CommonGrid>

      <HeaderContainer columns="1fr auto">
        <PendingFilters
          officesShortList={officesShortList}
          getOfficesShortList={getOfficesShortList}
          selectedOffice={selectedOffice}
          selectOffice={selectOffice}
          searchTerm={searchTerm}
          onSearchTermChange={onSearchTermChange}
          handleSearchByName={handleSearchByName}
          handleClearSearch={handleClearSearch}
          userCodeSearchTerm={userCodeSearchTerm}
          onUserCodeSearchTermChange={onUserCodeSearchTermChange}
          handleSearchByUserCode={handleSearchByUserCode}
          handleClearUserCodeSearch={handleClearUserCodeSearch}
          selectedRole={selectedRole}
          onRoleChange={handleRoleChange}
          selectedMembers={selectedMembers}
          selectedEmployeeType={selectedEmployeeType}
          onApprove={handleApprove}
          onReject={handleReject}
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          bulkUpdateFromPendingRequests={bulkUpdateFromPendingRequests}
          bulkUpdateFromPendingRequestsLoading={bulkUpdateFromPendingRequestsLoading}
        />
      </HeaderContainer>

      {/* {totalMembersCount && totalMembersCount > 0 && pageSize ? (
        <TopPagination
          itemName={t('pending_requests')}
          totalCount={totalMembersCount}
          currentPage={currentPageNumber}
          onPageChange={page => handlePageChange(page)}
          pageSize={pageSize}
          selectedMembers={selectedMembers}
        />
      ) : null} */}

      <PendingRequestMemberTable
        membersList={pendingRequests}
        onSelectionChange={handleSelectionChange}
        history={history}
        searchTerm={searchTerm}
        isLoading={isLoading || pendingRequestsLoading}
        selectedMembers={selectedMembers}
      />

      {/* {totalMembersCount && totalMembersCount > 0 && pageSize ? (
        <BottomPagination
          totalCount={totalMembersCount}
          currentPage={currentPageNumber}
          onPageChange={page => handlePageChange(page)}
          pageSize={pageSize}
        />
      ) : null} */}
    </div>
  );
};

export default PendingRequests;